import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const STEPS = ['Trade', 'Campaign', 'Ad', 'Budget', 'Launch'];

export default function ActivateWizard({ saved, connections, onFinish, onExit }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(saved?.step || 0);
  const [trade, setTrade] = useState(saved?.extra?.trade || null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [asset, setAsset] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [budget, setBudget] = useState(saved?.extra?.budget || 40);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (trade) api.get(`/campaigns?trade=${trade}`).then(({ data }) => setCampaigns((data.campaigns || data || []).slice(0, 6))).catch(() => {});
  }, [trade]);

  useEffect(() => {
    if (campaign) api.get(`/campaigns/${campaign.id}/assets`).then(({ data }) => {
      const assets = data.assets || data || [];
      setAsset(assets.find((a) => a.asset_type === 'Facebook Ad 1') || assets[0]);
    }).catch(() => {});
  }, [campaign]);

  const save = (n, completed = false) => {
    api.post('/guided/progress', { playbook_id: 'activate', step: n, completed, extra: { trade, budget } }).catch(() => {});
  };
  const go = (n) => { setStep(n); save(n); };

  const launch = async () => {
    setLaunching(true);
    save(step, true);
    onFinish();
    navigate(`/campaign/${campaign.id}`);
  };

  const connected = connections?.google?.connected || connections?.meta?.connected;

  return (
    <div className="max-w-md mx-auto" data-testid="activate-wizard">
      <div className="mb-2 flex items-center justify-between">
        <button onClick={onExit} className="text-sm text-gray hover:text-white" data-testid="wizard-exit-btn">← Journey</button>
        <p className="text-sm font-bold text-lime" data-testid="wizard-progress">Step {step + 1} of {STEPS.length}</p>
      </div>
      <div className="h-2 rounded-full bg-divider mb-8 overflow-hidden">
        <div className="h-full bg-lime transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
      </div>

      {step === 0 && (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-8">What's your trade?</h2>
          <div className="space-y-3">
            {['HVAC', 'Plumbing', 'Electrical'].map((t) => (
              <button key={t} onClick={() => { setTrade(t); go(1); }} data-testid={`trade-${t.toLowerCase()}-btn`}
                className="w-full py-4 rounded-xl border-2 border-divider text-white font-black text-lg hover:border-lime hover:text-lime transition-colors">{t}</button>
            ))}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Pick your first campaign</h2>
          <p className="text-light-gray mb-6">These are proven money-makers for {trade}.</p>
          <div className="space-y-3">
            {campaigns.map((c) => (
              <button key={c.id} onClick={() => { setCampaign(c); go(2); }} data-testid={`campaign-pick-${c.id}`}
                className="w-full p-4 rounded-xl border border-divider bg-card-bg text-left hover:border-lime transition-colors">
                <p className="text-white font-bold">{c.campaign_name}</p>
                <p className="text-sm text-gray">{c.avg_ticket} avg ticket</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Your recommended ad</h2>
          <p className="text-light-gray mb-6">Start with this one — it's already written for you.</p>
          {asset ? (
            <div className="rounded-xl border border-lime/40 bg-card-bg p-4 text-left mb-3" data-testid="recommended-asset">
              <p className="text-xs uppercase tracking-wide text-lime font-bold mb-2">{asset.asset_type}</p>
              <p className="text-sm text-light-gray whitespace-pre-line line-clamp-6">{(asset.content || asset.body || '').slice(0, 320)}…</p>
            </div>
          ) : <p className="text-gray mb-3">Loading…</p>}
          <button onClick={() => setMoreOpen(!moreOpen)} className="text-sm text-lime font-semibold mb-4" data-testid="more-assets-toggle">
            {moreOpen ? '− less' : '+ more assets'}
          </button>
          {moreOpen && <p className="text-sm text-gray mb-4">Your campaign includes 30 ready assets — emails, SMS, scripts, flyers and more. You'll see them all after launch.</p>}
          <button onClick={() => go(3)} data-testid="step-next-btn" className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90">Use this ad →</button>
        </div>
      )}

      {step === 3 && (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Set your daily budget</h2>
          <p className="text-light-gray mb-8">You can change this any time. You control the spend.</p>
          <p className="text-6xl font-black text-lime mb-6" data-testid="budget-value">${budget}<span className="text-xl text-gray font-normal">/day</span></p>
          <input type="range" min="10" max="200" step="5" value={budget} onChange={(e) => setBudget(Number(e.target.value))}
            className="w-full accent-[#c6f24e]" data-testid="budget-slider" />
          <div className="flex justify-between text-xs text-gray mt-1 mb-8"><span>$10</span><span>$200</span></div>
          <button onClick={() => go(4)} data-testid="step-next-btn" className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90">Review →</button>
        </div>
      )}

      {step === 4 && (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to launch</h2>
          <div className="rounded-xl border border-divider bg-card-bg p-5 text-left space-y-3 mb-6" data-testid="launch-review">
            <div className="flex justify-between"><span className="text-gray">Trade</span><span className="text-white font-bold">{trade}</span></div>
            <div className="flex justify-between"><span className="text-gray">Campaign</span><span className="text-white font-bold text-right">{campaign?.campaign_name}</span></div>
            <div className="flex justify-between"><span className="text-gray">Budget</span><span className="text-lime font-bold">${budget}/day</span></div>
            <div className="flex justify-between"><span className="text-gray">Runs in</span><span className="text-white font-bold">YOUR ad account</span></div>
          </div>
          {!connected && (
            <p className="text-sm text-orange mb-4" data-testid="launch-not-connected-note">
              Your ad account isn't connected yet — we'll open your campaign kit so everything is ready the moment you connect.
            </p>
          )}
          <button onClick={launch} disabled={launching || !campaign} data-testid="launch-btn"
            className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90 disabled:opacity-50">
            {launching ? 'Opening…' : 'Launch →'}
          </button>
        </div>
      )}
    </div>
  );
}
